import type { BlankPosition, MathConfig, MathOperation, MathProblem } from '../../types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function randInt(min: number, max: number): number {
  if (min > max) return min
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/** Distribute blanks: 40% result, 30% operand2, 30% operand1 */
function pickBlank(): BlankPosition {
  const r = Math.random()
  if (r < 0.4) return 'result'
  if (r < 0.7) return 'operand2'
  return 'operand1'
}

// ─── Per-operation generators ─────────────────────────────────────────────────
// Each returns a (operand1, operand2, result) triple or null if generation fails.
// `result` is always constrained to [resultMin, resultMax].
// Operands are kept ≤ 12 × result to stay kid-friendly.

function genAddition(min: number, max: number): [number, number, number] | null {
  const result   = randInt(min, max)
  const operand2 = randInt(0, result)
  const operand1 = result - operand2
  return [operand1, operand2, result]
}

function genSubtraction(min: number, max: number): [number, number, number] | null {
  // a - b = result  →  a = result + b
  const result   = randInt(Math.max(0, min), max)
  const operand2 = randInt(0, Math.min(result, max))
  const operand1 = result + operand2
  return [operand1, operand2, result]
}

function genMultiplication(min: number, max: number): [number, number, number] | null {
  // Avoid result = 0 so we don't get degenerate 0 × n = 0 problems
  const effectiveMin = Math.max(2, min)
  if (effectiveMin > max) return null

  const result  = randInt(effectiveMin, max)
  const factors: [number, number][] = []

  for (let a = 1; a <= Math.min(12, result); a++) {
    if (result % a === 0) {
      const b = result / a
      if (b <= 12) factors.push([a, b])
    }
  }

  if (factors.length === 0) return null
  const [operand1, operand2] = factors[randInt(0, factors.length - 1)]
  return [operand1, operand2, result]
}

function genDivision(min: number, max: number): [number, number, number] | null {
  // a ÷ b = result  →  a = result × b
  const effectiveMin = Math.max(1, min)
  if (effectiveMin > max) return null

  const result   = randInt(effectiveMin, max)
  const maxB     = Math.min(12, Math.floor(200 / result))
  if (maxB < 1) return null

  const operand2 = randInt(1, maxB)
  const operand1 = result * operand2
  return [operand1, operand2, result]
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Compute the answer for a problem's blank position */
export function correctAnswer(p: MathProblem): number {
  if (p.blankPosition === 'result')   return p.result
  if (p.blankPosition === 'operand1') return p.operand1
  return p.operand2
}

/** Generate a full problem set from the given config */
export function generateProblems(config: MathConfig): MathProblem[] {
  const { operations, resultMin, resultMax, problemCount } = config
  const problems: MathProblem[] = []
  const seen     = new Set<string>()
  const maxTries = problemCount * 30
  let tries      = 0

  while (problems.length < problemCount && tries < maxTries) {
    tries++

    const op = operations[randInt(0, operations.length - 1)] as MathOperation
    let triple: [number, number, number] | null = null

    switch (op) {
      case 'addition':       triple = genAddition(resultMin, resultMax);       break
      case 'subtraction':    triple = genSubtraction(resultMin, resultMax);    break
      case 'multiplication': triple = genMultiplication(resultMin, resultMax); break
      case 'division':       triple = genDivision(resultMin, resultMax);       break
    }

    if (!triple) continue

    const [op1, op2, res] = triple

    // Dedup by unique (op1, operation, op2) regardless of blank position
    const key = `${op1}${op}${op2}`
    if (seen.has(key)) continue
    seen.add(key)

    problems.push({
      operand1:      op1,
      operand2:      op2,
      operation:     op,
      result:        res,
      blankPosition: pickBlank(),
    })
  }

  return shuffle(problems)
}
